// The recovery phrase, seed and passkey PRF output never enter an API body.
import { deriveIdentityKey, mnemonicToSeed, signChallenge, type IdentityKeyMaterial } from "@link/crypto";
import type { SessionEstablishedResponse, SignupChallengeResponse } from "@link/shared";
import { fromBase64url } from "@link/shared/api/passkeys";
import { ApiCallError, fetchJson, writeSessionToken } from "./http";
import { primeIdentityFromSeed } from "./identity";
import { rememberCurrentSession } from "./device-session";
import { prepareSignupPasskey, type PasskeyTarget } from "./passkeys";
import { saveDevicePhrase, updateDeviceAccountProfile } from "./device-vault";

export type AuthSession = { token: string; accountXid: string };
const base64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
async function signed(identity: IdentityKeyMaterial, kind: "signup" | "login") {
  const public_key_b64 = base64(identity.publicKeyRaw);
  const challenge = await fetchJson<SignupChallengeResponse>(`/v1/auth/${kind}-challenge`, { method: "POST", token: null, body: { public_key_b64 } });
  const signature = await signChallenge(identity.privateKey, { nonce: Uint8Array.from(atob(challenge.nonce_b64), c => c.charCodeAt(0)), timestamp: BigInt(challenge.server_time_ms), sessionId: fromBase64url(challenge.session_id) });
  return { public_key_b64, session_id: challenge.session_id, nonce_b64: challenge.nonce_b64, signature_b64: base64(signature), platform: "web" };
}

export async function establishSession(info: AuthSession) {
  try { writeSessionToken(info.token); }
  catch { throw new Error("Your account is ready, but Link could not save this sign-in. Allow site storage, then sign in with your saved method or recovery phrase."); }
  await rememberCurrentSession();
}

async function saveIdentity(seed: Uint8Array, session: SessionEstablishedResponse): Promise<AuthSession> {
  try { await primeIdentityFromSeed(seed, session.account_id); }
  catch { throw new Error("Your account is ready, but this device could not save it. Allow site storage, then sign in with your saved method or recovery phrase."); }
  return { token: session.session_token, accountXid: session.account_id };
}

export async function runSignup(phrase: string, username: string, displayName: string | undefined, target: PasskeyTarget | "recovery" | "local" = "local"): Promise<AuthSession> {
  const seed = await mnemonicToSeed(phrase);
  try {
    const identity = await deriveIdentityKey(seed);
    // No account is created until registration, backup and proof are ready.
    const setup = target === "recovery" || target === "local" ? undefined : await prepareSignupPasskey(phrase, username, displayName, target);
    // Verify durable local recovery material BEFORE creating the server account.
    // Pending entries also allow retrying an ambiguous network result with this identity.
    await saveDevicePhrase(phrase, undefined, username);
    const proof = await signed(identity, "signup");
    let session: SessionEstablishedResponse;
    try {
      session = await fetchJson<SessionEstablishedResponse>("/v1/auth/signup", { method: "POST", token: null, body: { ...proof, username, display_name: displayName, passkey_setup: setup } });
    } catch (error) {
      // The server may have committed before a connection dropped. Proving
      // this same identity is safe; never generate a replacement identity here.
      if (!(error instanceof ApiCallError) || error.status >= 500 || error.envelope?.code === "auth.account_already_exists") {
        try { return await runLoginSeed(seed.slice(), undefined, phrase); } catch { /* report original failure */ }
      }
      throw error;
    }
    await saveDevicePhrase(phrase, session.account_id, username);
    return await saveIdentity(seed, session);
  } finally { seed.fill(0); }
}

export async function runLogin(phrase: string, expectedAccountXid?: string): Promise<AuthSession> {
  return runLoginSeed(await mnemonicToSeed(phrase), expectedAccountXid, phrase);
}

// Consumes/wipes the supplied seed even when login or local persistence fails.
export async function runLoginSeed(seed: Uint8Array, expectedAccountXid?: string, phrase?: string): Promise<AuthSession> {
  try {
    const identity = await deriveIdentityKey(seed);
    if (phrase) await saveDevicePhrase(phrase);
    const session = await fetchJson<SessionEstablishedResponse>("/v1/auth/login", { method: "POST", token: null, body: await signed(identity, "login") });
    if (expectedAccountXid && session.account_id !== expectedAccountXid) throw new Error("Account identity mismatch");
    if (phrase) {
      // Display metadata only; a temporary profile failure must not discard keys.
      const profile = await fetchJson<{ account_xid: string; username: string; display_name: string; avatar_url: string | null }>("/v1/me", { token: session.session_token, signal: AbortSignal.timeout(8000) }).catch(() => undefined);
      const username = profile?.account_xid === session.account_id && /^[a-z0-9_]{3,32}$/.test(profile.username) ? profile.username : undefined;
      await saveDevicePhrase(phrase, session.account_id, username);
      if (profile?.account_xid === session.account_id && username && typeof profile.display_name === "string") {
        await updateDeviceAccountProfile(session.account_id, { username, displayName: profile.display_name, avatarUrl: profile.avatar_url }).catch(() => undefined);
      }
    }
    return await saveIdentity(seed, session);
  } finally { seed.fill(0); }
}
